import { cn } from "@/lib/cn";
import type { ReactNode } from "react";

export interface Segment<T extends string> {
  value: T;
  label: ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  segments: ReadonlyArray<Segment<T>>;
  size?: "sm" | "md";
  block?: boolean;
  className?: string;
  "aria-label"?: string;
}

// SegmentedControl — pill toggle for 2-4 mutually exclusive options
// (Buy/Sell, chart ranges, list modes). For navigational tabs that drive the
// URL, use the Tabs primitive (Phase 4) instead.
export function SegmentedControl<T extends string>({
  value,
  onChange,
  segments,
  size = "md",
  block = false,
  className,
  ...aria
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={aria["aria-label"]}
      className={cn("inline-flex gap-1 rounded-xl bg-surface-2 p-1", block && "flex w-full", className)}
    >
      {segments.map((s) => {
        const active = s.value === value;
        return (
          <button
            key={s.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(s.value)}
            className={cn(
              "inline-flex items-center justify-center rounded-lg font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/55",
              size === "sm" ? "h-8 px-3 text-sm" : "h-9 px-4 text-sm",
              block && "flex-1",
              active ? "bg-surface text-fg shadow-sm" : "text-fg-muted hover:text-fg",
            )}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}
